import { Lock, Heart, Sparkles, GraduationCap, Leaf, Users, Rainbow, PawPrint, Calendar, MapPin, ArrowRight } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'
import { VerifiedBadge } from '@/components/VerifiedBadge'

// Static, presentational mock of a featured event, framed in a browser window and
// shown in the homepage hero. Not interactive — purely a polished preview. A fanned
// deck of simpler event cards sits behind it (see FANNED_EVENTS) for depth.

// Category chips shown above the event photo. Icon tints mirror the VENDOR_LOGOS
// convention in NonprofitHeroPreview.tsx.
const CHIPS = [
  { label: "Children's Health", Icon: Heart, className: 'text-rose-600' },
  { label: 'Arts & Culture', Icon: Sparkles, className: 'text-violet-600' },
  { label: 'Education', Icon: GraduationCap, className: 'text-sky-600' },
  { label: 'Environment', Icon: Leaf, className: 'text-emerald-600' },
]

type Chip = { label: string; Icon: LucideIcon; className: string }

// Mock events for the fanned deck behind the main card. Each is a full-height
// card matching the main one, kept deliberately distinct (photo, cause, org) to
// imply a full catalogue of events.
type FannedEvent = {
  image: string
  title: string
  org: string
  date: string
  location: string
  price: string
  chips: Chip[]
  wrapper: string
}

const FANNED_EVENTS: FannedEvent[] = [
  {
    image: '/hero-stack/gala-2.jpg',
    title: 'Future Leaders Gala',
    org: 'Bright Horizons Youth',
    date: 'Sat, Jun 14 · 7:00 PM',
    location: 'The Prospect · Chicago, IL',
    price: '$500',
    chips: [
      { label: 'Youth Development', Icon: Users, className: 'text-amber-600' },
      { label: 'Education', Icon: GraduationCap, className: 'text-sky-600' },
    ],
    // Center card — furthest back, sits straight and highest so its photo + chips peek over the top.
    wrapper: 'z-0 -translate-y-10 scale-[0.96] sm:-translate-y-20',
  },
  {
    image: '/hero-stack/gala-1.jpg',
    title: 'Pride Spring Benefit',
    org: 'Equality Forward Alliance',
    date: 'Fri, Apr 25 · 8:00 PM',
    location: 'Grand Hall · Austin, TX',
    price: '$300',
    chips: [
      { label: 'LGBTQ+', Icon: Rainbow, className: 'text-fuchsia-600' },
      { label: 'Arts & Culture', Icon: Sparkles, className: 'text-violet-600' },
    ],
    // Left card — fanned counter-clockwise, lifted so its photo edge shows.
    wrapper: 'z-[1] -translate-x-6 -translate-y-6 -rotate-3 scale-[0.94] sm:-translate-x-12 sm:-translate-y-10 sm:-rotate-6',
  },
  {
    image: '/hero-stack/gala-3.jpg',
    title: 'Paws & Whiskers Gala',
    org: 'Harbor Animal Rescue',
    date: 'Sun, Sep 21 · 6:00 PM',
    location: 'Harbor Club · Seattle, WA',
    price: '$250',
    chips: [
      { label: 'Animal Shelter', Icon: PawPrint, className: 'text-teal-600' },
      { label: 'Environment', Icon: Leaf, className: 'text-emerald-600' },
    ],
    // Right card — fanned clockwise, lifted so its photo edge shows.
    wrapper: 'z-[1] translate-x-6 -translate-y-6 rotate-3 scale-[0.94] sm:translate-x-12 sm:-translate-y-10 sm:rotate-6',
  },
]

// A chrome-less event card used only for the fanned deck behind the hero card.
// Stretched to the full height of the main card (absolute inset-0) so the deck
// reads as a stack of equal-length cards; the price row is pinned to the bottom.
function FannedCard({ image, title, org, date, location, price, chips, wrapper }: FannedEvent) {
  return (
    <div
      aria-hidden="true"
      className={`pointer-events-none absolute inset-0 flex origin-bottom flex-col overflow-hidden rounded-2xl border border-black/10 bg-card shadow-lg ${wrapper}`}
    >
      {/* Photo sits flush to the card top (no chrome/padding) so the part that
          peeks above the front card reveals the photo + chips, not blank padding. */}
      <div className="relative h-44 shrink-0 overflow-hidden">
        <img src={image} alt="" loading="lazy" decoding="async" className="h-full w-full object-cover" />
        <div className="absolute inset-x-0 top-0 h-16 bg-linear-to-b from-black/35 to-transparent" />
        <div className="absolute inset-x-3 top-3 flex flex-wrap gap-1.5">
          {chips.map(({ label, Icon, className }) => (
            <span
              key={label}
              className="flex items-center gap-1.5 rounded-full bg-card/90 px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
            >
              <Icon className={`size-3.5 ${className}`} />
              {label}
            </span>
          ))}
        </div>
      </div>
      <div className="flex flex-1 flex-col p-5">
        <h3 className="font-serif text-xl font-semibold leading-snug tracking-tight text-balance">
          {title}
        </h3>
        <span className="mt-1 flex items-center gap-1 text-sm font-medium text-primary">
          {org}
          <VerifiedBadge className="size-3.5" />
        </span>
        <div className="mt-4 space-y-1.5">
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <Calendar className="size-3.5 shrink-0" />
            <span>{date}</span>
          </div>
          <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
            <MapPin className="size-3.5 shrink-0" />
            <span className="truncate">{location}</span>
          </div>
        </div>
        <div className="mt-auto flex items-center justify-between border-t border-black/5 pt-4">
          <div className="leading-tight">
            <p className="text-xs text-muted-foreground">Tickets from</p>
            <p className="text-lg font-semibold text-foreground tabular-nums">{price}</p>
          </div>
          <span className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground">
            Get Tickets <ArrowRight className="size-4" />
          </span>
        </div>
      </div>
    </div>
  )
}

// Supporter avatars (free-to-use portraits stored in public/avatars).
const AVATARS = [
  '/avatars/avatar-1.jpg',
  '/avatars/avatar-2.jpg',
  '/avatars/avatar-3.jpg',
  '/avatars/avatar-4.jpg',
  '/avatars/avatar-5.jpg',
  '/avatars/avatar-6.jpg',
]

export default function HeroEventCard() {
  return (
    <div className="relative">
      {/* Fanned deck of other events peeking out behind the main card */}
      {FANNED_EVENTS.map(event => (
        <FannedCard key={event.title} {...event} />
      ))}

      <div className="relative z-10 overflow-hidden rounded-2xl border border-black/10 bg-card shadow-lg">
        {/* Browser chrome */}
        <div className="flex items-center gap-3 border-b border-black/5 bg-muted/30 px-4 py-3">
          <div className="flex items-center gap-1.5">
            <span className="size-3 rounded-full bg-[#ff5f57]" />
            <span className="size-3 rounded-full bg-[#febc2e]" />
            <span className="size-3 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex flex-1 items-center gap-1.5 rounded-md bg-card px-3 py-1 ring-1 ring-black/5">
            <Lock className="size-3 text-muted-foreground" />
            <span className="truncate text-xs text-muted-foreground">galaseason.com/beaumont-arts/renaissance-ball</span>
          </div>
        </div>

        <div className="p-5">
          {/* Event photo with cause badges (top) + supporter avatar stack (bottom) */}
          <div className="relative h-48 overflow-hidden rounded-xl">
            {/* LCP hero. Eager (no loading="lazy") + fetchPriority="high": React 19's
                SSR emits a single <link rel="preload" as="image" fetchpriority="high">
                for this — every other homepage <img> is loading="lazy" so it is NOT
                preloaded. This is the sole image preload on the page. */}
            <img src="/hero-gala.jpg" alt="" decoding="async" fetchPriority="high" className="h-full w-full object-cover" />
            <div className="absolute inset-x-0 top-0 h-16 bg-linear-to-b from-black/35 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 h-20 bg-linear-to-t from-black/40 to-transparent" />

            {/* Cause badges */}
            <div className="absolute inset-x-3 top-3 flex flex-wrap gap-1.5">
              {CHIPS.map(({ label, Icon, className }) => (
                <span
                  key={label}
                  className="flex items-center gap-1.5 rounded-full bg-card/90 px-2.5 py-1 text-xs font-medium shadow-sm backdrop-blur-sm"
                >
                  <Icon className={`size-3.5 ${className}`} />
                  {label}
                </span>
              ))}
            </div>

            {/* Supporter avatar stack */}
            <div className="absolute bottom-3 left-3 flex items-center gap-2 rounded-full bg-card/90 py-1 pl-1.5 pr-3 shadow-sm backdrop-blur-sm">
              <div className="flex -space-x-2">
                {AVATARS.map((src, i) => (
                  <img
                    key={i}
                    src={src}
                    alt=""
                    width={28}
                    height={28}
                    loading="lazy"
                    decoding="async"
                    className="size-7 rounded-full object-cover ring-2 ring-card"
                  />
                ))}
              </div>
              <span className="text-sm font-semibold text-primary">+45</span>
              <span className="text-sm font-medium text-muted-foreground">supporting</span>
            </div>
          </div>

          {/* Title + org */}
          <h3 className="mt-4 font-serif text-xl font-semibold leading-snug tracking-tight text-balance">
            Renaissance Ball 2026
          </h3>
          <span className="mt-1 flex items-center gap-1 text-sm font-medium text-primary">
            The Beaumont Arts Foundation
            <VerifiedBadge className="size-3.5" />
          </span>

          {/* Date + location */}
          <div className="mt-4 space-y-1.5">
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <Calendar className="size-3.5 shrink-0" />
              <span>Fri, May 17 · 8:00 PM</span>
            </div>
            <div className="flex items-center gap-1.5 text-sm text-muted-foreground">
              <MapPin className="size-3.5 shrink-0" />
              <span className="truncate">The Beaumont · New York, NY</span>
            </div>
          </div>

          {/* Price + CTA */}
          <div className="mt-5 flex items-center justify-between border-t border-black/5 pt-4">
            <div className="leading-tight">
              <p className="text-xs text-muted-foreground">Tickets from</p>
              <p className="text-lg font-semibold text-foreground tabular-nums">$750</p>
            </div>
            <span className="inline-flex h-9 items-center gap-1.5 justify-center rounded-lg bg-primary px-4 text-sm font-semibold text-primary-foreground">
              Get Tickets <ArrowRight className="size-4" />
            </span>
          </div>
        </div>
      </div>
    </div>
  )
}
